import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Delete,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { SchemeInstanceCard } from './SchemeInstanceCard';
import ChangeOfOwnershipCard from '../ChangeOfOwnershipCard';
import { anonymizeOwnerName, anonymizeCity, anonymizeState, anonymizeDistrict } from '../../utils/anonymize';

interface Owner {
  id: string;
  name: string;
  role_suggestion: string;
}

interface FacilitiesTableProps {
  facilities: any[];
  facilitiesWithSchemeData: any[];
  facilitiesData: any;
  facilitiesLoading: boolean;
  facilitiesSearch: string;
  facilitiesStateFilter: string;
  facilitiesPage: number;
  facilitiesPageSize: number;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  expandedFacilitiesScheme: Set<string>;
  expandedSchemeInstances: Set<string>;
  highlightedOwner: string | null;
  owners: Owner[];
  states: any[];
  ownershipChangesData: any;
  onSearchChange: (value: string) => void;
  onStateFilterChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSort: (field: string) => void;
  onFacilitySchemeExpand: (ccn: string) => void;
  onSchemeInstanceExpand: (key: string) => void;
  onOwnerPillClick: (ownerName: string) => void;
  onAddFacility?: () => void;
  onDeleteFacility?: (facility: any) => void;
  formatMoney: (amount: number) => string;
  sectionTitle?: string;
  showCollapseToggle?: boolean;
  demoMode?: boolean;
}

export const FacilitiesTable: React.FC<FacilitiesTableProps> = ({
  facilities,
  facilitiesWithSchemeData,
  facilitiesData,
  facilitiesLoading,
  facilitiesSearch,
  facilitiesStateFilter,
  facilitiesPage,
  facilitiesPageSize,
  sortField,
  sortDirection,
  expandedFacilitiesScheme,
  expandedSchemeInstances,
  highlightedOwner,
  owners,
  states,
  ownershipChangesData,
  onSearchChange,
  onStateFilterChange,
  onPageChange,
  onPageSizeChange,
  onSort,
  onFacilitySchemeExpand,
  onSchemeInstanceExpand,
  onOwnerPillClick,
  onAddFacility,
  onDeleteFacility,
  formatMoney,
  sectionTitle = 'Review Facilities',
  showCollapseToggle = true,
  demoMode = false
}) => {
  const [expandedOwners, setExpandedOwners] = React.useState<Set<string>>(new Set());
  const [sectionExpanded, setSectionExpanded] = React.useState<boolean>(true);

  const toggleExpandOwners = (ccn: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedOwners(prev => {
      const next = new Set(prev);
      if (next.has(ccn)) {
        next.delete(ccn);
      } else {
        next.add(ccn);
      }
      return next;
    });
  };

  const handleCollapseAll = () => {
    // Clear all expanded facility scheme cards
    expandedFacilitiesScheme.forEach(ccn => {
      onFacilitySchemeExpand(ccn);
    });
    // Clear all expanded scheme instance cards
    expandedSchemeInstances.forEach(key => {
      onSchemeInstanceExpand(key);
    });
    // Clear all expanded owners
    setExpandedOwners(new Set());
  };
  if (facilitiesLoading && !facilitiesData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading facilities...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, position: 'relative' }}>
      {facilitiesLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 10
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading facilities...
          </Typography>
        </Box>
      )}

      {/* Header with Collapse Toggle */}
      {showCollapseToggle ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
            cursor: 'pointer',
            '&:hover': { opacity: 0.7 }
          }}
          onClick={() => setSectionExpanded(!sectionExpanded)}
        >
          <IconButton size="small" sx={{ p: 0 }}>
            {sectionExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {sectionTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({facilitiesData?.total_count || 0} facilities)
          </Typography>
        </Box>
      ) : null}

      <Collapse in={showCollapseToggle ? sectionExpanded : true} timeout="auto" unmountOnExit={showCollapseToggle}>
        {/* Controls Row */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              size="small"
              placeholder="Search facilities..."
              value={facilitiesSearch}
              onChange={(e) => {
                onSearchChange(e.target.value);
              }}
              sx={{ minWidth: 250 }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by State</InputLabel>
              <Select
                value={facilitiesStateFilter}
                label="Filter by State"
                onChange={(e) => {
                  onStateFilterChange(e.target.value);
                }}
              >
                <MenuItem value="">
                  <em>All States</em>
                </MenuItem>
                {states?.map((stateData: any) => (
                  <MenuItem key={stateData.state} value={stateData.state}>
                    {stateData.state} ({stateData.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCollapseAll}
              disabled={expandedFacilitiesScheme.size === 0 && expandedSchemeInstances.size === 0}
            >
              Collapse All
            </Button>
            {onAddFacility && (
              <Button variant="contained" startIcon={<Add />} onClick={onAddFacility} size="small">
                Add Facility
              </Button>
            )}
          </Box>
        </Box>

        <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ cursor: 'pointer' }} onClick={() => onSort('org_name')}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Facility Name
                  {sortField === 'org_name' &&
                    (sortDirection === 'asc' ? (
                      <ArrowUpward sx={{ ml: 1, fontSize: 16 }} />
                    ) : (
                      <ArrowDownward sx={{ ml: 1, fontSize: 16 }} />
                    ))}
                </Box>
              </TableCell>
              {!demoMode && <TableCell>DBA Name</TableCell>}
              <TableCell>City</TableCell>
              <TableCell sx={{ width: '80px' }}>State</TableCell>
              <TableCell sx={{ width: '120px' }}>District</TableCell>
              <TableCell
                align="right"
                sx={{ cursor: 'pointer' }}
                onClick={() => onSort('total_billed')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Total Billed
                  {sortField === 'total_billed' &&
                    (sortDirection === 'asc' ? (
                      <ArrowUpward sx={{ ml: 1, fontSize: 16 }} />
                    ) : (
                      <ArrowDownward sx={{ ml: 1, fontSize: 16 }} />
                    ))}
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{ cursor: 'pointer' }}
                onClick={() => onSort('total_paid')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Total Paid
                  {sortField === 'total_paid' &&
                    (sortDirection === 'asc' ? (
                      <ArrowUpward sx={{ ml: 1, fontSize: 16 }} />
                    ) : (
                      <ArrowDownward sx={{ ml: 1, fontSize: 16 }} />
                    ))}
                </Box>
              </TableCell>
              <TableCell sx={{ width: '250px' }}>Owners</TableCell>
              <TableCell align="center" sx={{ width: '120px' }}>Still Owner</TableCell>
              {onDeleteFacility && <TableCell align="center">Delete</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {facilities?.map((facility: any) => {
              const hasHighlightedOwner = highlightedOwner && facility.owners?.includes(highlightedOwner);
              const facilityWithScheme = facilitiesWithSchemeData.find(
                (f) => f.ccn === facility.ccn
              );
              const hasSchemeData = !!facilityWithScheme;
              const schemeCount = facilityWithScheme?.scheme_stats?.in_case_count || 0;
              const hasNoSchemeInstances = hasSchemeData && schemeCount === 0;

              return (
                <React.Fragment key={facility.ccn}>
                  <Tooltip
                    title={hasNoSchemeInstances ? "This facility had no scheme dates during this defendant's ownership" : ""}
                    placement="top"
                    arrow
                  >
                    <TableRow
                      hover
                      onClick={hasSchemeData ? () => onFacilitySchemeExpand(facility.ccn) : undefined}
                      sx={{
                        bgcolor: hasNoSchemeInstances
                          ? 'grey.200'
                          : expandedFacilitiesScheme.has(facility.ccn)
                            ? 'warning.50'
                            : hasHighlightedOwner
                              ? 'primary.50'
                              : 'inherit',
                        '&:hover': {
                          bgcolor: hasNoSchemeInstances
                            ? 'grey.300'
                            : expandedFacilitiesScheme.has(facility.ccn)
                              ? 'warning.100'
                              : hasHighlightedOwner
                                ? 'primary.100'
                                : 'grey.50'
                        },
                        cursor: hasSchemeData ? 'pointer' : 'default',
                        borderLeft: expandedFacilitiesScheme.has(facility.ccn)
                          ? '4px solid'
                          : 'none',
                        borderLeftColor: 'warning.main',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {hasSchemeData && (
                          <Box sx={{ p: 0.5 }}>
                            {expandedFacilitiesScheme.has(facility.ccn) ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </Box>
                        )}
                        <Box sx={{ flex: 1 }}>
                          {facility.org_name}
                          {hasSchemeData && (
                            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                              <Chip
                                label={`${facilityWithScheme?.scheme_stats?.in_case_count || 0}/${facilityWithScheme?.scheme_stats?.total_instances || 0} scheme`}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                              {/* Hide CCN chip in demo mode */}
                              {!demoMode && (
                                <Chip
                                  label={`CCN: ${facility.ccn}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    backgroundColor: 'grey.100',
                                    borderColor: 'grey.300',
                                    color: 'grey.600',
                                    fontSize: '0.7rem'
                                  }}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    {!demoMode && <TableCell>{facility.dba_name || '-'}</TableCell>}
                    <TableCell><Typography variant="caption">{demoMode ? anonymizeCity(facility.city) || '-' : facility.city || '-'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{demoMode ? anonymizeState(facility.state) || '-' : facility.state || '-'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{demoMode ? anonymizeDistrict(facility.federal_district_abbrev, anonymizeState(facility.state)) || '-' : facility.federal_district_abbrev || '-'}</Typography></TableCell>
                    <TableCell align="right">{formatMoney(facility.total_billed)}</TableCell>
                    <TableCell align="right">{formatMoney(facility.total_paid)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {(() => {
                          const ownersList = facility.owners || [];
                          const isExpanded = expandedOwners.has(facility.ccn);
                          const maxVisible = 3;
                          const visibleOwners = isExpanded ? ownersList : ownersList.slice(0, maxVisible);
                          const remainingCount = ownersList.length - maxVisible;

                          return (
                            <>
                              {visibleOwners.map((ownerName: string, idx: number) => {
                                const isHighlighted = highlightedOwner === ownerName;
                                const owner = owners?.find((o: Owner) => o.name === ownerName);
                                const isLead = owner?.role_suggestion === 'LEAD';
                                const displayName = demoMode ? anonymizeOwnerName(ownerName) : ownerName;

                                return (
                                  <Chip
                                    key={idx}
                                    label={displayName}
                                    size="small"
                                    clickable
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onOwnerPillClick(ownerName);
                                    }}
                                    color={isLead ? 'primary' : 'default'}
                                    variant={isHighlighted ? 'filled' : 'outlined'}
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: '24px',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        bgcolor: isLead ? 'primary.100' : 'grey.100'
                                      },
                                      ...(isHighlighted && {
                                        bgcolor: isLead ? 'primary.main' : 'grey.800',
                                        color: 'white',
                                        '&:hover': {
                                          bgcolor: isLead ? 'primary.dark' : 'grey.900'
                                        }
                                      })
                                    }}
                                  />
                                );
                              })}
                              {remainingCount > 0 && (
                                <Chip
                                  label={isExpanded ? 'Show less' : `+${remainingCount} more`}
                                  size="small"
                                  clickable
                                  onClick={(e) => toggleExpandOwners(facility.ccn, e)}
                                  sx={{
                                    fontSize: '0.75rem',
                                    height: '24px',
                                    cursor: 'pointer',
                                    bgcolor: 'grey.200',
                                    '&:hover': {
                                      bgcolor: 'grey.300'
                                    }
                                  }}
                                />
                              )}
                              {ownersList.length === 0 && '-'}
                            </>
                          );
                        })()}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const ownershipData = ownershipChangesData[facility.ccn];
                        // Use still_owner field from backend which checks current ownership in most recent data
                        const stillOwner = ownershipData?.still_owner ?? true; // Default to Yes if no data

                        return (
                          <Typography
                            variant="body2"
                            sx={{
                              color: stillOwner ? 'success.main' : 'error.main',
                              fontWeight: 600
                            }}
                          >
                            {stillOwner ? 'Yes' : 'No'}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    {onDeleteFacility && (
                      <TableCell align="center">
                        <Tooltip title="Remove facility from case">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteFacility(facility);
                            }}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                bgcolor: 'error.50'
                              }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                  </Tooltip>

                  {/* Expandable scheme analysis row */}
                  {hasSchemeData && expandedFacilitiesScheme.has(facility.ccn) && (
                    <TableRow>
                      <TableCell colSpan={onDeleteFacility ? 10 : 9} sx={{ py: 0, borderBottom: 'none' }}>
                        <Collapse
                          in={expandedFacilitiesScheme.has(facility.ccn)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 3, pl: 4, backgroundColor: 'grey.25' }}>
                            {/* Ownership Changes Card */}
                            {ownershipChangesData[facility.ccn] && (
                              <Box sx={{ mb: 3 }}>
                                <ChangeOfOwnershipCard
                                  ccn={facility.ccn}
                                  ownershipData={ownershipChangesData[facility.ccn]}
                                  schemeInstances={facilityWithScheme?.scheme_instances || []}
                                  demoMode={demoMode}
                                />
                              </Box>
                            )}

                            {facilityWithScheme?.scheme_instances
                              ?.sort(
                                (a: any, b: any) =>
                                  new Date(a.date).getTime() - new Date(b.date).getTime()
                              )
                              .map((instance: any) => {
                                const instanceKey = `${facility.ccn}-${instance.id}`;
                                return (
                                  <SchemeInstanceCard
                                    key={instanceKey}
                                    instance={instance}
                                    instanceKey={instanceKey}
                                    expanded={expandedSchemeInstances.has(instanceKey)}
                                    onToggle={onSchemeInstanceExpand}
                                    formatMoney={formatMoney}
                                  />
                                );
                              })}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

        {/* Pagination controls */}
        {facilitiesData && (
          <TablePagination
            component="div"
            count={facilitiesData.total_count || 0}
            page={facilitiesPage}
            onPageChange={(_, newPage) => onPageChange(newPage)}
            rowsPerPage={facilitiesPageSize}
            onRowsPerPageChange={(e) => onPageSizeChange(+e.target.value)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            showFirstButton
            showLastButton
          />
        )}
      </Collapse>
    </Box>
  );
};